/* eslint-disable @angular-eslint/prefer-standalone */
import { Component, OnInit, inject } from '@angular/core';
import {
	LanguageService,
	PageService,
	PageLoaderService,
	NotificationService,
	onLine$,
	AppLanguage,
	useDefaultDateLocale,
	AppBaseComponent,
	PreloaderService,
	TelemetryService,
} from '@aston/foundation';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ActivatedRoute, Router, NavigationStart, NavigationEnd } from '@angular/router';
import { defineLocale, enGbLocale } from 'ngx-bootstrap/chronos';
import { AIChatPanelService, avaAnchor } from '@aston/ai-chat';
import { filter, take, switchMap, tap } from 'rxjs/operators';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { frLocale, IPageAnchor } from '@aston/foundation';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { timer } from 'rxjs';

import { AuthenticationService } from './authentication-module/services';
import { AppStoreActions, AppStoreSelectors } from './root-store/app-store';
import * as RoutesDefinitions from './routes-definitions';
import { AppConstants } from './app.constants';


@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	providers: [
		TranslatePipe
	],
	standalone: false
})
export class AppComponent extends AppBaseComponent implements OnInit {
	private telemetryService = inject(TelemetryService);
	translatePipe = inject(TranslatePipe);
	private bsLocaleService = inject(BsLocaleService);
	private preloader = inject(PreloaderService);
	modal = inject(NgbModal);
	private store = inject(Store);
	protected aiPanels = inject(AIChatPanelService);

	pageAnchors: IPageAnchor[] = [];

	appIsReady$ = this.store.select(AppStoreSelectors.selectIsReady);
	currentUser$ = this.store.select(AppStoreSelectors.selectCurrentUser);
	bootstrapError$ = this.store.select(AppStoreSelectors.selectBootstrapError);

	onLine$ = onLine$;

	constructor() {
		const authenticationService = inject(AuthenticationService);
		const translateService = inject(TranslateService);
		const languageService = inject(LanguageService);
		const pageService = inject(PageService);
		const notificationService = inject(NotificationService);
		const pageLoaderService = inject(PageLoaderService);
		const route = inject(ActivatedRoute);
		const router = inject(Router);

		super(translateService, languageService, pageService, notificationService, pageLoaderService, route, router);

		defineLocale(AppLanguage.FR, frLocale);
		defineLocale(AppLanguage.FR, enGbLocale);

		authenticationService.getIsAuthorized().pipe(
			filter(Boolean),
			switchMap(_ => this.currentUser$),
			switchMap(user => this.translateService.stream('Home.Page.Hello', { name: user.fullName.firstName })),
			tap(message => this.preloader.withPreClosingMessage(`${message} !`)),
			tap(_ => this.preloader.closePreloaderWhenPreloadersFinished().then(evt => {
				this.telemetryService.event('[App] Preloader closed', evt, { duration: evt.elapsed  })
			})),
			take(1))
			.subscribe()

		languageService.language$.subscribe(current => {
			this.bsLocaleService.use(current);
			useDefaultDateLocale(current)
		});

		pageService.pageAnchors$.subscribe(anchors => {
			this.pageAnchors = [
				{ ...avaAnchor, onClick: () => this.aiPanels.toggleSideChat() },
				...anchors,
			];
		});

		router.events.pipe(
			filter(event => event instanceof NavigationStart),
			filter(_ => this.modal.hasOpenModals())
		).subscribe(_ => {
			alert(this.translatePipe.transform('App.NavigationMessage'));
			this.modal.dismissAll();
		});

		router.events.pipe(
			filter(event => event instanceof NavigationEnd),
		).subscribe(_ => {
			pageService.resetAnchors();

			let url = window.location.pathname;

			if (!url.includes(RoutesDefinitions.AuthenticationRoutePath)) {
				const tenantId = authenticationService.getTenantId();

				// the tenant id is not a part of the route so we remove it from the stored one
				url = url.replace('/' + tenantId + '', '');
				this.store.dispatch(AppStoreActions.UpdateUserSessionRequest({userSession: {lastRoute: url}}));
			}
		});

		translateService
			.get('Filters.DateExpression')
			.pipe(take(1))
			.subscribe(keys => {
				AppConstants.updateDateExpressionWithTranslation(keys);
			});
	}

	ngOnInit() {
		super.ngOnInit();

		timer(0, AppConstants.SECOND)
			.pipe(
				switchMap(_ => this.store.select(AppStoreSelectors.selectIsInactive)),
				filter(isInactive => !isInactive)
			)
			.subscribe(_ => {
				this.store.dispatch(AppStoreActions.UpdateInactivityTimer());
			});
	}
}
